"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _useFormElementValidation = _interopRequireDefault(require("../../hooks/useFormElementValidation"));
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
var _useLabel2 = _interopRequireDefault(require("../../hooks/useLabel"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
var defaultConfig = {
  toolbar: {
    items: ['bulletedList', 'numberedList', '|', 'undo', 'redo'],
    shouldNotGroupWhenFull: true
  }
};
var Editor = function Editor(props) {
  var controlledId = props.id,
    name = props.name,
    controlledValue = props.value,
    initialLabel = props.label,
    helper = props.helper,
    maxChar = props.maxChar,
    readOnly = props.readOnly,
    disabled = props.disabled,
    required = props.required,
    locale = props.locale,
    addon = props.addon,
    addonPosition = props.addonPosition,
    onChange = props.onChange,
    onError = props.onError,
    className = props.className;
  var id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;
  var editorRef = _react.default.useRef(null);
  var textareaRef = _react.default.useRef(id || name);
  var _React$useState = _react.default.useState(controlledValue || ''),
    value = _React$useState[0],
    setValue = _React$useState[1];
  var _React$useState2 = _react.default.useState(false),
    error = _React$useState2[0],
    setError = _React$useState2[1];
  var _React$useState3 = _react.default.useState(readOnly || disabled || false),
    isReadOnly = _React$useState3[0],
    setIsReadOnly = _React$useState3[1];
  var _useFormElementValida = (0, _useFormElementValidation.default)(value, {
      required: required,
      maxChar: maxChar
    }),
    errors = _useFormElementValida[0];
  var _useLabel = (0, _useLabel2.default)(initialLabel, _extends({}, props, {
      id: id,
      value: value
    })),
    label = _useLabel.label;
  var _React$useState4 = _react.default.useState(false),
    editorLoaded = _React$useState4[0],
    setEditorLoaded = _React$useState4[1];
  var _ref = editorRef.current || {},
    CKEditor = _ref.CKEditor,
    ClassicEditor = _ref.ClassicEditor;
  _react.default.useEffect(function () {
    editorRef.current = {
      CKEditor: require('@ckeditor/ckeditor5-react').CKEditor,
      ClassicEditor: require('@sm360/ckeditor5-uikit-build/build/ckeditor')
    };
    editorRef.current.ClassicEditor.defaultConfig = defaultConfig;
    setEditorLoaded(true);
  }, []);
  _react.default.useEffect(function () {
    setError(errors.length > 0);
  }, [errors]);
  _react.default.useEffect(function () {
    setIsReadOnly(readOnly || disabled || false);
  }, [readOnly, disabled]);
  _react.default.useEffect(function () {
    setError(errors.length > 0);
    if (typeof onError === 'function') {
      onError({
        name: textareaRef.current.name,
        errors: errors.length ? errors : []
      });
    }
  }, [errors, onError]);
  var handleChange = function handleChange(v) {
    setValue(v);
    if (typeof onChange === 'function') {
      onChange(v, textareaRef.current.name);
    }
  };
  var inputAddon = /*#__PURE__*/_react.default.isValidElement(addon) ? addon : /*#__PURE__*/_react.default.createElement('span', {}, addon);
  var containerClasses = (0, _clsx.default)('uk-form-item', 'uk-textarea uk-editor', disabled && 'disabled', addon && 'has-info', addon && "has-info--" + addonPosition, (helper && helper.length > 0 || error) && 'has-helper', error && 'has-error', className);
  return editorLoaded ? /*#__PURE__*/_react.default.createElement("div", {
    className: containerClasses
  }, label, /*#__PURE__*/_react.default.createElement("div", {
    className: "flex-container"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-inputtable"
  }, /*#__PURE__*/_react.default.createElement(CKEditor, {
    editor: ClassicEditor,
    data: controlledValue,
    onReady: function onReady(editor) {
      editor.language = locale;
    },
    onChange: function onChange(event, editor) {
      var data = editor.getData();
      handleChange(data);
    },
    onBlur: function onBlur(event, editor) {
      // console.log( 'Blur.', editor );
    },
    onFocus: function onFocus(event, editor) {
      // console.log( 'Focus.', editor );
    },
    disabled: isReadOnly
  })), maxChar && /*#__PURE__*/_react.default.createElement("div", {
    className: "helper helper--counter"
  }, "Characters left: ", maxChar - value.length > 0 ? maxChar - value.length : 0), addon && /*#__PURE__*/_react.default.createElement("div", {
    className: (0, _clsx.default)('uk-inputtable__addon', addonPosition)
  }, inputAddon)), (helper && helper.length > 0 || error) && /*#__PURE__*/_react.default.createElement("small", {
    className: "helper"
  }, error ? errors : helper)) : null;
};
Editor.propTypes = {
  /**
   * ID of the Editor (used to bind the label to the Editor)
   * NB: the ID will be randomly generated if not set.
   */
  id: _propTypes.default.string,
  /**
   * Name of the Editor (used instead of the ID when submitted)
   */
  name: _propTypes.default.string,
  /**
   * Default value of the Editor
   */
  value: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number]),
  /**
   * Label of the Editor
   */
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Editor placeholder
   */
  placeholder: _propTypes.default.string,
  /**
   * Helper text, is replaced by a message in case of error
   */
  helper: _propTypes.default.string,
  /**
   * Remove the placeholder (if any), replace it with a floating label
   */
  floatLabel: _propTypes.default.bool,
  /**
   * Enable the resizing of the Editor
   */
  resizable: _propTypes.default.bool,
  /**
   * Set and display a maximun of characters the user can input before triggering an error
   */
  maxChar: _propTypes.default.number,
  /**
   * Set the Editor as read-only
   */
  readOnly: _propTypes.default.bool,
  /**
   * Set the Editor as disabled
   */
  disabled: _propTypes.default.bool,
  /**
   * Form-validation: set the Editor as required
   */
  required: _propTypes.default.bool,
  /**
   * Add an informative box next to the input element
   */
  addon: _propTypes.default.string,
  /**
   * Position the info relative to the input element
   */
  addonPosition: _propTypes.default.oneOf(['left', 'right']),
  /**
   * Callback function onChange
   */
  onChange: _propTypes.default.func,
  /**
   * Callback function onInput
   */
  onInput: _propTypes.default.func,
  /**
   * Callback function onBlur
   */
  onBlur: _propTypes.default.func,
  /**
   * Callback function onError
   */
  onError: _propTypes.default.func
};
Editor.defaultProps = {
  placeholder: '',
  helper: '',
  floatLabel: false,
  readOnly: false,
  disabled: false,
  required: false
};
var _default = exports.default = Editor;